/**
 * Orgus tools, drawn for what they answer. The state summary is a set of grouped figures
 * (colour only where something runs, waits or failed); a session list is session rows with
 * their status, project and last activity; machines, pipelines and projects likewise; the
 * things that need you as a list of what and where; spend as figures and budget meters; a
 * change as its receipt. Anything else falls back to the generic table or card (shape.ts).
 * Every list shows "12 of 77" when the hub shortened it to fit.
 */
import { AlertTriangle, Check, CircleDot, FolderGit2, GitBranch, Hand, Server, TerminalSquare, Workflow } from '@orgus/ui/icons';
import type { LucideIcon } from '@orgus/ui/icons';
import { Link } from 'react-router';
import type { ReactNode } from 'react';
import type { AssistantStep, Session } from '@orgus/protocol';
import { CostMeter, RelativeTime, RowStatus, cn, formatInt, formatMoney, formatPercent, sessionRowStatus } from '@orgus/ui';
import type { RowStatusProps } from '@orgus/ui';
import { useMachines, useProjects } from '../../../lib/store.ts';
import { Fact, ShowMore } from './templates.tsx';

type Rec = Record<string, unknown>;
const isObj = (v: unknown): v is Rec => typeof v === 'object' && v !== null && !Array.isArray(v);
const n = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const s = (v: unknown): string => (typeof v === 'string' ? v : '');

/** The answer as an object, or null (text, cut or not JSON). */
export function answerOf(step: Pick<AssistantStep, 'result'>): Rec | null {
  if (!step.result) return null;
  try {
    const v = JSON.parse(step.result) as unknown;
    return isObj(v) ? v : null;
  } catch {
    return null;
  }
}

/** "12 of 77" under a list the hub shortened. */
function Shown({ shown, total, noun }: { shown: number; total: number | null; noun: string }) {
  if (total === null || total <= shown) return <Fact>{`${formatInt(shown)} ${noun}`}</Fact>;
  return <Fact>{`${formatInt(shown)} of ${formatInt(total)} ${noun}`}</Fact>;
}

/** A row in one of these lists: status first, then the title, then at most two quiet facts. */
function Row({ status, title, meta, to }: { status: ReactNode; title: string; meta: ReactNode[]; to?: string }) {
  const body = (
    <>
      <span className="w-28 shrink-0">{status}</span>
      <span className="min-w-0 flex-1 truncate text-fg" title={title}>
        {title}
      </span>
      {meta.filter(Boolean).slice(0, 2).map((m, i) => (
        <span key={i} className="hidden shrink-0 text-fg-subtle sm:inline">
          {m}
        </span>
      ))}
    </>
  );
  const cls = 'flex min-h-control-sm items-center gap-3 rounded-control px-2 text-caption';
  return to ? (
    <Link to={to} className={cn(cls, 'hover:bg-hover')}>
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  );
}

const status = (p: Pick<RowStatusProps, 'tone' | 'icon' | 'label'>) => <RowStatus {...p} focusable={false} />;

// ---------------------------------------------------------------------------
// State summary
// ---------------------------------------------------------------------------

type Tone = 'working' | 'attention' | 'danger' | null;
const TONE_DOT: Record<Exclude<Tone, null>, string> = { working: 'bg-status-working', attention: 'bg-status-attention', danger: 'bg-status-danger' };

function Figure({ label, value, tone }: { label: string; value: string; tone?: Tone }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-caption">
      <dt className="flex min-w-0 items-center gap-2 text-fg-muted">
        <span className={cn('size-1.5 shrink-0 rounded-pill', tone ? TONE_DOT[tone] : 'bg-border-strong')} aria-hidden />
        <span>{label}</span>
      </dt>
      <dd className={cn('num', tone ? 'font-medium text-fg' : 'text-fg-muted')}>{value}</dd>
    </div>
  );
}

function Group({ icon: Icon, title, children }: { icon: LucideIcon; title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2 rounded-card border border-border-subtle p-3">
      <h4 className="flex items-center gap-2 text-caption font-medium text-fg">
        <Icon className="size-3.5 text-fg-subtle" aria-hidden />
        {title}
      </h4>
      <dl className="flex flex-col gap-1">{children}</dl>
    </section>
  );
}

const when = (count: number | null, t: Exclude<Tone, null>): Tone => (count && count > 0 ? t : null);
const int = (v: number | null) => (v === null ? 'none' : formatInt(v));

export function SummaryView({ a }: { a: Rec }) {
  const ss = isObj(a.sessions) ? a.sessions : {};
  const p = isObj(a.pipelines) ? a.pipelines : {};
  const m = isObj(a.machines) ? a.machines : {};
  const sp = isObj(a.spend) ? a.spend : null;
  const needs = n(a.needsYou);
  return (
    <div className="flex flex-col gap-2">
      {needs !== null && (
        <p className={cn('flex items-center gap-2 text-caption', needs > 0 ? 'text-status-attention-ink' : 'text-fg-muted')}>
          <Hand className="size-3.5" aria-hidden />
          {needs > 0 ? `${formatInt(needs)} ${needs === 1 ? 'thing needs' : 'things need'} you` : 'Nothing needs you'}
          {n(a.approvalsPending) ? ` · ${formatInt(n(a.approvalsPending)!)} approvals pending` : ''}
        </p>
      )}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Group icon={TerminalSquare} title="Sessions">
          <Figure label="Working" value={int(n(ss.working))} tone={when(n(ss.working), 'working')} />
          <Figure label="Needs you" value={int(n(ss.waiting))} tone={when(n(ss.waiting), 'attention')} />
          <Figure label="Idle" value={int(n(ss.idle))} />
          <Figure label="Ended" value={int(n(ss.ended))} />
        </Group>
        <Group icon={Workflow} title="Agents and pipelines">
          <Figure label="Subagents running" value={int(n(a.subagentsRunning))} tone={when(n(a.subagentsRunning), 'working')} />
          <Figure label="Workflows running" value={int(n(a.workflowsRunning))} tone={when(n(a.workflowsRunning), 'working')} />
          <Figure label="Items in flight" value={int(n(p.inFlight))} tone={when(n(p.inFlight), 'working')} />
          <Figure label="Failed in a day" value={int(n(p.failedLastDay))} tone={when(n(p.failedLastDay), 'danger')} />
        </Group>
        <Group icon={Server} title="Machines">
          <Figure label="Online" value={`${int(n(m.online))} of ${int(n(m.total))}`} />
          <Figure label="Stale" value={int(n(m.stale))} tone={when(n(m.stale), 'attention')} />
          <Figure label="Offline" value={int(n(m.offline))} tone={when(n(m.offline), 'danger')} />
          <Figure label="Jobs running" value={int(n(a.jobsRunning))} tone={when(n(a.jobsRunning), 'working')} />
        </Group>
        <Group icon={CircleDot} title="Spend">
          <Figure label="Today" value={sp && n(sp.today) !== null ? formatMoney(n(sp.today)!) : 'not tracked'} />
          <Figure label="This month" value={sp && n(sp.month) !== null ? formatMoney(n(sp.month)!) : 'not tracked'} />
          <Figure label="Projects" value={int(n(a.projects))} />
          <Figure label="Missions running" value={int(n(a.missionsRunning))} tone={when(n(a.missionsRunning), 'working')} />
        </Group>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lists
// ---------------------------------------------------------------------------

function SessionsView({ a }: { a: Rec }) {
  const projects = useProjects();
  const machines = useMachines();
  const list = (Array.isArray(a.sessions) ? a.sessions : []).filter(isObj) as unknown as Session[];
  const total = n(a.total) ?? n(a.sessionsTotal);
  if (!list.length) return <Fact>No sessions match.</Fact>;
  return (
    <div className="flex flex-col gap-1">
      <Shown shown={list.length} total={total} noun="sessions" />
      <div className="flex flex-col">
        <ShowMore
          items={list}
          first={8}
          noun="sessions"
          render={(x) => (
            <Row
              key={x.id}
              to={`/sessions?session=${encodeURIComponent(x.id)}`}
              status={status(sessionRowStatus(x.status))}
              title={x.title || x.id.slice(0, 8)}
              meta={[
                projects.find((p) => p.id === x.projectId)?.name ?? machines.find((m) => m.id === x.machineId)?.name ?? null,
                typeof x.lastActivity === 'number' ? <RelativeTime ts={x.lastActivity} /> : null,
              ]}
            />
          )}
        />
      </div>
    </div>
  );
}

const LIVENESS: Record<string, Pick<RowStatusProps, 'tone' | 'icon' | 'label'>> = {
  online: { tone: 'neutral', icon: 'done', label: 'Online' },
  stale: { tone: 'attention', icon: 'warning', label: 'Stopped reporting' },
  offline: { tone: 'danger', icon: 'offline', label: 'Offline' },
};

function MachinesView({ a }: { a: Rec }) {
  const list = (Array.isArray(a.machines) ? a.machines : []).filter(isObj);
  if (!list.length) return <Fact>No machines.</Fact>;
  return (
    <div className="flex flex-col gap-1">
      <Shown shown={list.length} total={n(a.machinesTotal)} noun="machines" />
      {list.map((m) => (
        <Row
          key={s(m.id)}
          to={`/machines/${encodeURIComponent(s(m.id))}`}
          status={status(LIVENESS[s(m.liveness)] ?? (m.online ? LIVENESS.online! : LIVENESS.offline!))}
          title={s(m.name) || s(m.hostname)}
          meta={[n(m.sessionsLive) !== null ? `${formatInt(n(m.sessionsLive)!)} live sessions` : null, n(m.load) !== null && n(m.cpus) ? `${formatPercent(Math.min(1, n(m.load)! / n(m.cpus)!))} load` : null]}
        />
      ))}
    </div>
  );
}

const PIPELINE: Record<string, Pick<RowStatusProps, 'tone' | 'icon' | 'label'>> = {
  running: { tone: 'working', icon: 'working', label: 'Running' },
  idle: { tone: 'neutral', icon: 'idle', label: 'Idle' },
  paused: { tone: 'neutral', icon: 'paused', label: 'Paused' },
  down: { tone: 'danger', icon: 'failed', label: 'Down' },
  error: { tone: 'danger', icon: 'failed', label: 'Error' },
};

function PipelinesView({ a }: { a: Rec }) {
  const projects = useProjects();
  const list = (Array.isArray(a.pipelines) ? a.pipelines : []).filter(isObj);
  if (!list.length) return <Fact>No pipelines.</Fact>;
  return (
    <div className="flex flex-col gap-1">
      <Shown shown={list.length} total={n(a.pipelinesTotal)} noun="pipelines" />
      {list.map((p, i) => {
        const items = Array.isArray(p.items) ? p.items.filter(isObj) : [];
        const recent = Array.isArray(p.recent) ? p.recent.filter(isObj) : [];
        const failed = recent.filter((x) => x.state === 'failed').length;
        return (
          <Row
            key={`${s(p.projectId)}${i}`}
            to="/pipelines"
            status={status(PIPELINE[s(p.state)] ?? PIPELINE.idle!)}
            title={`${projects.find((x) => x.id === p.projectId)?.name ?? 'Project'} · ${s(p.title) || s(p.adapter)}`}
            meta={[items.length ? `${formatInt(items.length)} in flight` : null, failed ? <span className="text-status-danger-ink">{`${formatInt(failed)} failed recently`}</span> : null]}
          />
        );
      })}
    </div>
  );
}

function ProjectsView({ a }: { a: Rec }) {
  const list = (Array.isArray(a.projects) ? a.projects : []).filter(isObj);
  if (!list.length) return <Fact>No projects are watched.</Fact>;
  return (
    <div className="flex flex-col gap-1">
      <Shown shown={list.length} total={n(a.projectsTotal)} noun="projects" />
      {list.map((p) => {
        const checkouts = (Array.isArray(p.checkouts) ? p.checkouts : []).filter(isObj);
        const main = checkouts.find((c) => c.kind === 'main') ?? checkouts[0];
        const total = n(p.checkoutsTotal) ?? checkouts.length;
        const sync = main ? [n(main.ahead) ? `${n(main.ahead)} ahead` : '', n(main.behind) ? `${n(main.behind)} behind` : '', n(main.dirty) ? `${n(main.dirty)} changed` : ''].filter(Boolean).join(', ') : '';
        return (
          <Row
            key={s(p.id)}
            to={`/projects/${encodeURIComponent(s(p.id))}`}
            status={
              <span className="flex items-center gap-2 text-fg-muted">
                <FolderGit2 className="size-3.5" aria-hidden />
                {formatInt(total)} {total === 1 ? 'checkout' : 'checkouts'}
              </span>
            }
            title={s(p.name)}
            meta={[
              main?.branch ? (
                <span className="flex items-center gap-1">
                  <GitBranch className="size-3" aria-hidden />
                  {s(main.branch)}
                </span>
              ) : null,
              sync || null,
            ]}
          />
        );
      })}
    </div>
  );
}

const NEED_ICON: Record<string, LucideIcon> = { session: Hand, approval: Hand, pipeline: AlertTriangle, machine: Server, worktree: GitBranch };

function NeedsView({ a }: { a: Rec }) {
  const list = (Array.isArray(a.items) ? a.items : []).filter(isObj);
  if (!list.length) return <Fact>Nothing needs you.</Fact>;
  return (
    <ul className="flex flex-col gap-1">
      <Shown shown={list.length} total={n(a.itemsTotal)} noun={list.length === 1 ? 'thing' : 'things'} />
      <ShowMore
        items={list}
        first={6}
        noun="items"
        render={(x, i) => {
          const Icon = NEED_ICON[s(x.kind)] ?? Hand;
          const danger = x.kind === 'pipeline' || x.kind === 'machine';
          return (
            <li key={i} className="flex items-start gap-3 rounded-control px-2 py-1 text-caption">
              <Icon className={cn('mt-1 size-3.5 shrink-0', danger ? 'text-status-danger' : 'text-status-attention')} aria-hidden />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-fg">{s(x.title)}</span>
                <span className="truncate text-fg-subtle">{[s(x.where), s(x.detail)].filter(Boolean).join(' · ')}</span>
              </span>
              {typeof x.at === 'number' && (
                <span className="shrink-0 text-fg-subtle">
                  <RelativeTime ts={x.at} />
                </span>
              )}
            </li>
          );
        }}
      />
    </ul>
  );
}

function CostsView({ a }: { a: Rec }) {
  const budgets = (Array.isArray(a.budgets) ? a.budgets : []).filter(isObj);
  const top = (Array.isArray(a.topSessions) ? a.topSessions : []).filter(isObj);
  const money = (v: unknown) => {
    if (n(v) !== null) return n(v)!;
    return isObj(v) && n(v.cost) !== null ? n(v.cost)! : null;
  };
  return (
    <div className="flex flex-col gap-3">
      <dl className="grid grid-cols-2 gap-2">
        {(['today', 'month'] as const).map((k) => (
          <div key={k} className="flex flex-col rounded-card border border-border-subtle px-3 py-2">
            <dt className="text-caption text-fg-subtle">{k === 'today' ? 'Today' : 'This month'}</dt>
            <dd className="text-lead text-fg num">{money(a[k]) !== null ? formatMoney(money(a[k])!) : 'not tracked'}</dd>
          </div>
        ))}
      </dl>
      {budgets.slice(0, 4).map((b, i) => {
        const st = isObj(b.status) ? b.status : {};
        return <CostMeter key={i} size="sm" label={s(b.name) || s(b.scope) || 'Budget'} cost={n(st.spent) ?? n(st.cost) ?? 0} budget={n(b.limit) ?? n(b.amount) ?? undefined} period={s(b.period) || undefined} />;
      })}
      {top.length > 0 && (
        <div className="flex flex-col gap-1">
          <Fact>Top sessions</Fact>
          {top.slice(0, 5).map((x, i) => (
            <div key={i} className="flex items-center gap-3 px-2 text-caption">
              <span className="min-w-0 flex-1 truncate text-fg">{s(x.title) || s(x.sessionId) || s(x.id)}</span>
              <span className="text-fg-muted num">{money(x.total) !== null ? formatMoney(money(x.total)!) : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** A change: what happened, in one line, and where it is audited. */
function ReceiptView({ step, a }: { step: AssistantStep; a: Rec | null }) {
  const failed = step.outcome === 'error';
  const msg = (a && (s(a.message) || (isObj(a.error) ? s(a.error.what) : ''))) || step.message || (failed ? 'It reported a failure.' : 'Done.');
  return (
    <p className={cn('flex items-center gap-2 text-caption', failed ? 'text-status-danger-ink' : 'text-fg')}>
      {failed ? <AlertTriangle className="size-3.5 shrink-0" aria-hidden /> : <Check className="size-3.5 shrink-0 text-status-success" aria-hidden />}
      <span className="min-w-0 flex-1">{msg}</span>
      {step.auditId && <span className="shrink-0 font-mono text-fg-subtle">{step.auditId}</span>}
    </p>
  );
}

const VIEWS: Record<string, (p: { a: Rec }) => ReactNode> = {
  'state.summary': SummaryView,
  'sessions.list': SessionsView,
  'machines.list': MachinesView,
  'pipelines.list': PipelinesView,
  'projects.list': ProjectsView,
  'needs_you.list': NeedsView,
  'costs.summary': CostsView,
};

/** The purpose-built view for this Orgus step, or null to use the generic one. */
export function orgusView(step: AssistantStep): ReactNode | null {
  const a = answerOf(step);
  const View = VIEWS[step.capability];
  if (View && a && a.available !== false) return <View a={a} />;
  if (step.tier !== 'read' && step.capability !== 'api.call') return <ReceiptView step={step} a={a} />;
  return null;
}
